// components/Leaderboard/ClassificaPietre.jsx
// Classifica speciale lega Pietre (id_league = 4)
// Mostra totale punti ottavo posto per ogni membro.
// Toccando la propria riga si apre il dettaglio: corridore scelto per
// tappa e se indovinato.

import { useEffect, useState } from 'react'
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native'
import { supabase } from '../../lib/supabase'
import { useThemeColors } from '../../lib/useThemeColors'
import { fonts } from '../../lib/theme'

const PIETRE_LEAGUE_ID = 4

function UserDetailPanel({ tc, styles, userId }) {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.rpc('fn_pietre_user_detail', { p_id_user: userId })
      .then(({ data }) => {
        setDetail(data ?? [])
        setLoading(false)
      })
  }, [userId])

  if (loading) return (
    <View style={styles.detailCenter}>
      <ActivityIndicator color={tc.rosaText} size="small" />
    </View>
  )

  if (!detail || detail.length === 0) return (
    <View style={styles.detailCenter}>
      <Text style={styles.detailEmpty}>Nessuna pick ancora registrata</Text>
    </View>
  )

  return (
    <View style={styles.detailWrap}>
      {detail.map((d) => (
        <View key={d.id_stage} style={[styles.detailRow, { borderTopColor: tc.border }]}>
          <Text style={[styles.detailStage, { color: tc.textMuted }]}>
            Tappa {d.id_stage_number}
          </Text>
          <Text style={[styles.detailRider, { color: tc.text }]} numberOfLines={1}>
            {d.rider_picked_name ?? '—'}
          </Text>
          <Text style={{ fontSize: 15 }}>
            {d.is_correct ? '✅' : '❌'}
          </Text>
        </View>
      ))}
    </View>
  )
}

export default function ClassificaPietre({ currentUserId }) {
  const tc = useThemeColors()
  const styles = makeStyles(tc)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedUserId, setExpandedUserId] = useState(null)

  useEffect(() => {
    supabase.rpc('fn_pietre_standings')
      .then(({ data }) => {
        setRows(data ?? [])
        setLoading(false)
      })
  }, [])

  if (loading) return (
    <View style={styles.center}>
      <ActivityIndicator color={tc.rosaText} />
    </View>
  )

  if (rows.length === 0) return (
    <View style={styles.center}>
      <Text style={styles.empty}>Nessun dato disponibile</Text>
    </View>
  )

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerLabel}>🪨 Classifica Ottavo Posto</Text>
        <Text style={styles.headerSub}>+1 punto per ogni ottavo indovinato</Text>
      </View>

      {/* Rows */}
      {rows.map((r, i) => {
        const isMe = r.id_user === currentUserId
        const isExpanded = expandedUserId === r.id_user
        const medal = r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : null

        const Row = (
          <View
            style={[
              styles.row,
              { borderBottomColor: tc.border },
              isMe && { backgroundColor: tc.rosaSubtle },
              isMe && isExpanded && { borderBottomWidth: 0 },
            ]}
          >
            {/* Posizione */}
            <View style={styles.rankWrap}>
              {medal
                ? <Text style={styles.medal}>{medal}</Text>
                : <Text style={[styles.rank, { color: tc.textMuted }]}>{r.rank}</Text>
              }
            </View>

            {/* Nome */}
            <Text style={[styles.name, { color: isMe ? tc.rosaText : tc.text }, isMe && { fontWeight: '700' }]} numberOfLines={1}>
              {r.display_name}{isMe ? ' (tu)' : ''}
            </Text>

            {/* Stats */}
            <View style={styles.statsWrap}>
              <Text style={[styles.pts, { color: r.total_points > 0 ? tc.rosaText : tc.textMuted }]}>
                {r.total_points} pt
              </Text>
              <Text style={[styles.ratio, { color: tc.textMuted }]}>
                {r.correct_picks}/{r.total_picks}
              </Text>
            </View>

            {isExpanded && (
              <Text style={[styles.chevron, { color: tc.textMuted }]}>▲</Text>
            )}
          </View>
        )

        return (
          <View key={r.id_user}>
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() => setExpandedUserId(isExpanded ? null : r.id_user)}
            >
              {Row}
            </TouchableOpacity>

            {isExpanded && (
              <UserDetailPanel tc={tc} styles={styles} userId={r.id_user} />
            )}
          </View>
        )
      })}

      <Text style={[styles.footer, { color: tc.textMuted }]}>
        Formato: punti totali · indovinati/totali pick
      </Text>
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container:   { paddingBottom: 16 },
  center:      { padding: 32, alignItems: 'center' },
  empty:       { color: tc.textMuted, fontSize: 13 },
  header:      { padding: 16, paddingBottom: 12 },
  headerLabel: { fontFamily: fonts.condensedBold, fontSize: 15, color: tc.text, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 },
  headerSub:   { fontSize: 11, color: tc.textMuted },
  row:         { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 1, gap: 10 },
  rankWrap:    { width: 28, alignItems: 'center' },
  medal:       { fontSize: 18 },
  rank:        { fontFamily: fonts.condensedBold, fontSize: 14 },
  name:        { flex: 1, fontSize: 13, fontFamily: fonts.condensedBold },
  statsWrap:   { alignItems: 'flex-end', gap: 2 },
  pts:         { fontFamily: fonts.condensedBold, fontSize: 14 },
  ratio:       { fontSize: 10 },
  chevron:     { fontSize: 10, marginLeft: 6 },
  footer:      { fontSize: 10, textAlign: 'center', paddingTop: 12, paddingHorizontal: 16, opacity: 0.6 },

  detailWrap:   { backgroundColor: tc.rosaSubtle, paddingHorizontal: 16, paddingBottom: 8 },
  detailRow:    { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderTopWidth: 1, gap: 10 },
  detailStage:  { width: 70, fontSize: 11, fontFamily: fonts.condensedBold },
  detailRider:  { flex: 1, fontSize: 12 },
  detailCenter: { padding: 16, alignItems: 'center' },
  detailEmpty:  { fontSize: 11, color: tc.textMuted },
})